import { introwise } from 'common/types';
import settings from '../settings';
import { eventUrl, circleUrl, communityUrl, circleUrlCommunity, absoluteUrl } from '../urls';

declare global {
  interface Window {
    analytics: SegmentAnalytics.AnalyticsJS;
  }
}

class Tracking {
  static setup() {
    window.analytics.load(settings.segment.writeKey);
  }

  static reset() {
    window.analytics.reset();
  }

  static trackPage() {
    window.analytics.page();
  }

  // We track all user traits for integrations that are client-only
  // We also update users traits on the server to ensure
  // they are captured for server-only integrations.
  static trackUser(user: introwise.User) {
    if (user) {
      window.analytics.identify(user.id, {
        name: user.name,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        avatar: user.photo,
        createdAt: user.createdAt,
        pronouns: user.pronouns,
      });
    }
  }

  static trackEventCreated(event: introwise.Event) {
    try {
      window.analytics.track('Event Created', {
        id: event.id,
        title: event.title,
        privacy: event.privacy,
        url: absoluteUrl(eventUrl({ eventId: event.id, eventSlug: event.slug })),
        ...(event.communityId && { communityId: event.communityId }),
      });
    } catch (error) {
      console.warn('Failed to track Event Created event');
    }
  }

  static trackEventJoined(event: introwise.Event) {
    try {
      window.analytics.track('Event Joined', {
        id: event.id,
        title: event.title,
        url: absoluteUrl(eventUrl({ eventId: event.id, eventSlug: event.slug })),
      });
    } catch (error) {
      console.warn('Failed to track Event Joined event');
    }
  }

  static trackEventLeft(event: introwise.Event) {
    try {
      window.analytics.track('Event Left', {
        id: event.id,
        title: event.title,
        url: absoluteUrl(eventUrl({ eventId: event.id, eventSlug: event.slug })),
      });
    } catch (error) {
      console.warn('Failed to track Event Left event');
    }
  }

  static trackCircleJoined({
    circleId,
    eventId,
    eventTitle,
    title,
  }: {
    circleId: string;
    eventId: string;
    eventTitle: string;
    title: string;
  }) {
    try {
      window.analytics.track('Circle Joined', {
        id: circleId,
        title,
        eventId,
        eventTitle,
        url: absoluteUrl(circleUrl({ eventId, circleId })),
      });
    } catch (error) {
      console.warn('Failed to track Circle Joined event');
    }
  }

  static trackCircleLeft({
    circleId,
    eventId,
    eventTitle,
    title,
  }: {
    circleId: string;
    eventId: string;
    eventTitle: string;
    title: string;
  }) {
    try {
      window.analytics.track('Circle Left', {
        id: circleId,
        title,
        eventId,
        eventTitle,
        url: absoluteUrl(circleUrl({ eventId, circleId })),
      });
    } catch (error) {
      console.warn('Failed to track Circle Left event');
    }
  }

  static trackCircleCreated({
    circleId,
    eventId,
    eventTitle,
    title,
    withDate,
  }: {
    circleId: string;
    eventId: string;
    eventTitle: string;
    title: string;
    withDate: boolean;
  }) {
    try {
      window.analytics.track('Circle Created', {
        id: circleId,
        title,
        eventId,
        eventTitle,
        url: absoluteUrl(circleUrl({ eventId, circleId })),
        withDate,
      });
    } catch (error) {
      console.warn('Failed to track Circle Created event');
    }
  }

  static trackCircleDeleted({
    circleId,
    eventId,
    eventTitle,
    title,
  }: {
    circleId: string;
    eventId: string;
    eventTitle: string;
    title: string;
  }) {
    try {
      window.analytics.track('Circle Deleted', {
        id: circleId,
        title,
        eventId,
        eventTitle,
        url: absoluteUrl(circleUrl({ eventId, circleId })),
      });
    } catch (error) {
      console.warn('Failed to track Circle Deleted event');
    }
  }

  static trackCircleMessage({
    circleId,
    eventId,
    messageLength,
  }: {
    circleId: string;
    eventId: string;
    messageLength: number;
  }) {
    try {
      window.analytics.track('Circle Message', {
        circleId,
        eventId,
        messageLength,
      });
    } catch (error) {
      console.warn('Failed to track Circle Message event');
    }
  }

  static trackIcebreakerMessage({ eventId, messageLength }: { eventId: string; messageLength: number }) {
    try {
      window.analytics.track('Icebreaker Message', {
        eventId,
        messageLength,
      });
    } catch (error) {
      console.warn('Failed to track Icebreaker Message event');
    }
  }

  static trackCommunityCreated(community: introwise.Community) {
    try {
      window.analytics.track('Community Created', {
        id: community.id,
        slug: community.slug,
        name: community.name,
        private: community.private,
        url: absoluteUrl(communityUrl({ communitySlug: community.slug })),
      });
    } catch (error) {
      console.warn('Failed to track Community Created event');
    }
  }

  static trackCommunityJoined(community: introwise.Community) {
    try {
      window.analytics.track('Community Joined', {
        id: community.id,
        name: community.name,
        url: absoluteUrl(communityUrl({ communitySlug: community.slug })),
      });
    } catch (error) {
      console.warn('Failed to track Community Joined event');
    }
  }

  static trackCommunityLeft(community: introwise.Community) {
    try {
      window.analytics.track('Community Left', {
        id: community.id,
        name: community.name,
        url: absoluteUrl(communityUrl({ communitySlug: community.slug })),
      });
    } catch (error) {
      console.warn('Failed to track Community Left event');
    }
  }

  static trackCommunityJoinRequestSent(community: introwise.Community, messageLength: number) {
    try {
      window.analytics.track('Community Join Request Sent', {
        id: community.id,
        name: community.name,
        messageLength,
        url: absoluteUrl(communityUrl({ communitySlug: community.slug })),
      });
    } catch (error) {
      console.warn('Failed to track Community Join Request Sent event');
    }
  }

  static trackCommunityJoinRequestApproved(community: introwise.Community, requestorId: string) {
    try {
      window.analytics.track('Community Join Request Approved', {
        id: community.id,
        name: community.name,
        requestorId,
        url: absoluteUrl(communityUrl({ communitySlug: community.slug })),
      });
    } catch (error) {
      console.warn('Failed to track Community Join Request Approved event');
    }
  }

  static trackCommunityJoinRequestDeclined(community: introwise.Community, requestorId: string) {
    try {
      window.analytics.track('Community Join Request Declined', {
        id: community.id,
        name: community.name,
        requestorId,
        url: absoluteUrl(communityUrl({ communitySlug: community.slug })),
      });
    } catch (error) {
      console.warn('Failed to track Community Join Request Declined event');
    }
  }

  static trackCommunityCircleJoined({
    circleId,
    communityId,
    communitySlug,
    communityName,
    title,
  }: {
    circleId: string;
    communityId: string;
    communitySlug: string;
    communityName: string;
    title: string;
  }) {
    try {
      window.analytics.track('Community Circle Joined', {
        id: circleId,
        title,
        communityId,
        communityName,
        url: absoluteUrl(circleUrlCommunity({ communitySlug, circleId })),
      });
    } catch (error) {
      console.warn('Failed to track Community Circle Joined event');
    }
  }

  static trackCommunityCircleLeft({
    circleId,
    communityId,
    communitySlug,
    communityName,
    title,
  }: {
    circleId: string;
    communityId: string;
    communitySlug: string;
    communityName: string;
    title: string;
  }) {
    try {
      window.analytics.track('Community Circle Left', {
        id: circleId,
        title,
        communityId,
        communityName,
        url: absoluteUrl(circleUrlCommunity({ communitySlug, circleId })),
      });
    } catch (error) {
      console.warn('Failed to track Community Circle Left event');
    }
  }

  static trackCommunityCircleCreated({
    circleId,
    communityId,
    communitySlug,
    communityName,
    title,
    withDate,
  }: {
    circleId: string;
    communityId: string;
    communitySlug: string;
    communityName: string;
    title: string;
    withDate: boolean;
  }) {
    try {
      window.analytics.track('Community Circle Created', {
        id: circleId,
        title,
        communityId,
        communityName,
        url: absoluteUrl(circleUrlCommunity({ communitySlug, circleId })),
        withDate,
      });
    } catch (error) {
      console.warn('Failed to track Community Circle Created event');
    }
  }

  static trackCommunityCircleDeleted({
    circleId,
    communityId,
    communitySlug,
    communityName,
    title,
  }: {
    circleId: string;
    communityId: string;
    communitySlug: string;
    communityName: string;
    title: string;
  }) {
    try {
      window.analytics.track('Community Circle Deleted', {
        id: circleId,
        title,
        communityId,
        communityName,
        url: absoluteUrl(circleUrlCommunity({ communitySlug, circleId })),
      });
    } catch (error) {
      console.warn('Failed to track Community Circle Deleted event');
    }
  }

  static trackCommunityCircleMessage({
    circleId,
    communityId,
    messageLength,
  }: {
    circleId: string;
    communityId: string;
    messageLength: number;
  }) {
    try {
      window.analytics.track('Community Circle Message', {
        circleId,
        communityId,
        messageLength,
      });
    } catch (error) {
      console.warn('Failed to track Community Circle Message event');
    }
  }

  static trackCommunityInterestPersonalized(community: introwise.Community, interests: introwise.Interest[]) {
    try {
      window.analytics.track('Community Interest Personalized', {
        id: community.id,
        name: community.name,
        url: absoluteUrl(communityUrl({ communitySlug: community.slug })),
        interestsCount: interests ? interests.length : 0,
      });
    } catch (error) {
      console.warn('Failed to track Community Interest Personalized event');
    }
  }

  static trackCommunityCategoriesEdited(community: introwise.Community) {
    try {
      window.analytics.track('Community Interest Personalized', {
        id: community.id,
        name: community.name,
        url: absoluteUrl(communityUrl({ communitySlug: community.slug })),
        categoriesCount: community.collections ? community.collections.length : 0,
      });
    } catch (error) {
      console.warn('Failed to track Community Interest Personalized event');
    }
  }

  static trackSignedIn(userId: string, provider: 'linkedin' | 'google' | 'facebook' | 'github') {
    try {
      window.analytics.identify(userId);
      window.analytics.track('Signed In', { provider });
    } catch (error) {
      console.warn('Failed to track Signed In event');
    }
  }
}

export default Tracking;
